'use client';

import { useQueryClient } from '@tanstack/react-query';
import Select from '@ui/Select';
import { removeUndefinedRecursive } from '@util/firestore/products';
import { addUserSizes } from '@util/firestore/users';
import { useToastContext } from 'context/ToastContext';
import { BootsIcon } from 'i/measurement/boot-icon';
import { HelmetIcon } from 'i/measurement/helmet-icon';
import { GearIcon } from 'i/measurement/gear-icon';
import PantIcon from 'i/measurement/pant-icon';
import { BOOT_SIZES, LETTER_SIZES, PANT_SIZES } from 'models/shared';
import { UserDocument, UserSizes } from 'models/user';
import { useState } from 'react';

type EditSizesFormProps = {
  userDoc: UserDocument;
  dismiss: () => void;
};

export default function EditSizesForm({
  userDoc,
  dismiss,
}: EditSizesFormProps) {
  const [newSizes, setNewSizes] = useState<UserSizes | null>(
    userDoc.sizes ?? null
  );
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccessToast } = useToastContext();

  const icons = {
    helmet: <HelmetIcon className="h-[5rem] w-[5rem]" />,
    boots: <BootsIcon className="h-[5rem] w-[5rem]" />,
    gear: <GearIcon className="h-[5rem] w-[5rem]" />,
    pants: <PantIcon className="h-[5rem] w-[5rem]" />,
  };

  const categories = [
    {
      id: 'helmet',
      label: 'Helmet',
      icon: icons.helmet,
      options: LETTER_SIZES,
    },
    {
      id: 'jersey',
      label: 'Jersey / Shirt',
      icon: icons.gear,
      options: LETTER_SIZES,
    },
    {
      id: 'pants',
      label: 'Pants',
      icon: icons.pants,
      options: PANT_SIZES.M,
    },
    { id: 'boots', label: 'Boots', icon: icons.boots, options: BOOT_SIZES.M },
  ];

  const onCreateSizes = async () => {
    if (userDoc?.uid) {
      setIsSaving(true);
      const data = removeUndefinedRecursive(newSizes); // remove undefined values from form
      await addUserSizes(userDoc.uid, data);
      dismiss();
      showSuccessToast('Size Saved Successfully', 'bottom-center');
      queryClient.invalidateQueries({
        queryKey: ['authUser'],
      });
      setIsSaving(false);
    }
  };

  return (
    <div className="flex h-fit w-full flex-col justify-between">
      <div className="flex h-[40rem] w-[50rem] flex-col gap-y-8 p-8">
        {categories.map((category) => (
          <div
            className="flex w-full items-center justify-between gap-x-48"
            key={category.id}
          >
            <div className="flex items-center gap-x-6">
              {category.icon}

              <span className="whitespace-nowrap text-[2.1rem] font-semibold text-black">
                {category.label === 'jersey'
                  ? 'Jersey / Shirt'
                  : category.label}
              </span>
            </div>

            <div className="flex w-[13rem] justify-end">
              <Select
                options={category.options.map((size: string | number) => ({
                  id: String(size),
                  value: String(size),
                  label: String(size),
                }))}
                value={
                  newSizes?.[category.id as keyof UserSizes] as
                    | string
                    | undefined
                }
                onChange={(option) => {
                  setNewSizes({
                    ...newSizes,
                    [category.id]: option.id,
                  } as UserSizes);
                }}
                placeholder={`Size`}
                className="!text-[2.1rem]"
                allowEmpty={false}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="flex w-full justify-between gap-8 border-t border-zinc-200 p-8">
        <button
          type="button"
          className="flex h-[5rem] w-full items-center justify-center rounded-[1.5rem] bg-brand-secondary text-[1.8rem] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-secondary-darker disabled:opacity-50"
          onClick={onCreateSizes}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save My Sizes'}
        </button>
      </div>
    </div>
  );
}
